import { MAP_EDITED, Mixpanel } from 'libs/third-party/Mixpanel';
import { getMapTypeForEvent } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { SettingsAccordionToggle, TextSetting } from 'modules/Editor/components/SideMenuSettings/ChartSettings';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FontSettings, TextSettings } from 'widgets/ChartWidgets/DataTableChartCommon/TextStyleSettings';
import { useWidget } from 'widgets/sdk';

import { MapWidgetData, ScaleType } from '../MapWidget.types';

export const LegendSettings = () => {
  const {
    mapType,
    scaleType = ScaleType.Steps,
    legend: { enabled },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const onLegendToggle = () => {
    updateWidget({
      legend: {
        enabled: !enabled,
      },
    });
    Mixpanel.track(MAP_EDITED, {
      styling_property: MAP_EDITED.stylingProperty.Legend,
      styling_value: !enabled ? MAP_EDITED.stylingValue.LegendOn : MAP_EDITED.stylingValue.LegendOff,
      map_type: getMapTypeForEvent(MAP_EDITED, mapType, scaleType),
    });
  };

  return (
    <SettingsAccordionToggle
      onToggle={onLegendToggle}
      isChecked={enabled}
      title={'Legend'}
      accordionPanelProps={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Title />
      <FontStyle />
      <TextStyle />
    </SettingsAccordionToggle>
  );
};

const Title = () => {
  const {
    legend: {
      title: { text },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const { t } = useTranslation('editor_side_menu_settings', {
    useSuspense: false,
    keyPrefix: 'mapChartSettings',
  });

  const onTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    updateWidget({
      legend: {
        title: { text: newText },
      },
    });
  };

  return (
    <TextSetting
      label={t('legendTitleLabel')}
      value={text}
      onChange={onTextChange}
      placeholder={'Legend title'}
      data-testid={'chart-title-input'}
    />
  );
};

const FontStyle = () => {
  const {
    legend: {
      title: {
        style: { fontFamily, fontSize },
      },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();

  const onFontFamilyChange = (fontFamily: string) => {
    updateWidget({
      legend: {
        title: {
          style: {
            fontFamily: fontFamily,
          },
        },
        itemStyle: {
          fontFamily: fontFamily,
        },
      },
    });
  };

  const onFontSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const fontSize = event.target.value;
    updateWidget({
      legend: {
        title: {
          style: {
            fontSize: `${fontSize}px`,
          },
        },
        itemStyle: {
          fontSize: `${fontSize}px`,
        },
      },
    });
  };

  return (
    <FontSettings
      fontFamily={fontFamily}
      fontSize={fontSize}
      onChangeFontFamily={onFontFamilyChange}
      onChangeFontSize={onFontSizeChange}
    />
  );
};

const TextStyle = () => {
  const {
    legend: {
      title: {
        style: { color, fontWeight, fontStyle, fontFamily },
      },
    },
    updateWidget,
  } = useWidget<MapWidgetData>();

  // Color onChanges happen too often to be traced in Mixpanel
  const onFontColorChange = (fontColor: string) => {
    updateWidget({
      legend: {
        title: {
          style: {
            color: fontColor,
          },
        },
        itemStyle: {
          color: fontColor,
        },
      },
    });
  };

  const onFontWeightChange = (fontWeight: string) => {
    updateWidget({
      legend: {
        title: {
          style: {
            fontWeight: fontWeight,
          },
        },
        itemStyle: {
          fontWeight: fontWeight,
        },
      },
    });
  };

  const onFontStyleChange = (fontStyle: string) => {
    updateWidget({
      legend: {
        title: {
          style: {
            fontStyle: fontStyle,
          },
        },
        itemStyle: {
          fontStyle: fontStyle,
        },
      },
    });
  };

  return (
    <TextSettings
      fontFamily={fontFamily}
      fontColor={color}
      fontWeight={fontWeight}
      fontStyle={fontStyle}
      onFontColorChange={onFontColorChange}
      onFontWeightChange={onFontWeightChange}
      onFontStyleChange={onFontStyleChange}
    />
  );
};
